const { getVamkuDisplayPrice, isVamkuPeriodActive } = window.opOrderDetailsVamku;
const {
  ORDER_EDIT_LINE_LIMIT,
  buildOrderUpdatePayload,
  getChangedLineIds
} = window.opOrderDetailsEditor;

const SAVE_BANNER_HIDE_DELAY_MS = 5000;

class OpOrderDetails extends HTMLElement {
  constructor() {
    super();
    this.retailerId = this.dataset.retailerId;
    this.orderId = new URL(window.location.href).searchParams.get('id');
    this.axOrderId = new URL(window.location.href).searchParams.get('axId');
    if (this.axOrderId) {
      this.endpoint = `/apps/ax/order-portal/retailers/${this.retailerId}/orders?axOrderId=${this.axOrderId}`;
    } else {
      this.endpoint = `/apps/ax/order-portal/retailers/${this.retailerId}/orders/${this.orderId}`;
    }
    this.fetchOrderDetails();
    this.handleActions();
    window.addEventListener('beforeunload', (event) => {
      if (!this.isSaving) return;
      event.preventDefault();
      event.returnValue = '';
    });
  }

  async fetchOrderDetails({ soft = false } = {}) {
    if (!soft) this.classList.add('op-loading');
    try {
      const response = await fetch(this.endpoint);
      const responseJson = await response.json();
      if (!responseJson.order) {
        if (soft) throw new Error('Order not found');
        console.error('Order not found');
        this.renderOrderNotFound();
        return;
      }

      this.order = responseJson.order;
      this.renderOrderSummary();
      if (soft) {
        await this.renderOrderProductsPreservingView();
      } else {
        await this.fetchCampaignPeriod();
        await this.renderOrderProducts();
      }
    } catch (error) {
      if (soft) throw error;
      console.error('Error fetching order details:', error);
      this.renderOrderNotFound();
    } finally {
      if (!soft) this.classList.remove('op-loading');
    }
  }

  renderOrderNotFound() {
    const pageContent = document.querySelector('.op-order-details .page__content');
    if (pageContent) {
      pageContent.innerHTML = `<div class="rte article-content mt-12 mx-auto">${window.opOrderDetailsTexts.order_not_found}</div>`;
    }
    document.querySelector('.op-order-details .page__subtitle')?.classList.add('hidden');
  }

  async renderOrderProductsPreservingView() {
    const scrollY = window.scrollY;
    const expandedVariantIds = new Set(
      Array.from(this.querySelectorAll('.op-table-row.show-more-data .qty-input__input'))
        .map(input => input.dataset.vid)
    );

    await this.renderOrderProducts();

    this.querySelectorAll('.op-table-row').forEach(row => {
      const variantId = row.querySelector('.qty-input__input')?.dataset.vid;
      if (expandedVariantIds.has(variantId)) {
        row.classList.add('show-more-data');
      }
    });
    window.scrollTo(0, scrollY);
  }

  async fetchCampaignPeriod() {
    if (!this.order?.campaignDisplayId) {
      this.campaignOfficialPeriod = null;
      this.renderCampaignPeriod();
      return;
    }

    try {
      const response = await fetch(`/apps/ax/order-portal/retailers/${this.retailerId}/campaigns`);
      if (!response.ok) throw new Error(`Campaign request failed with status ${response.status}`);

      const responseJson = await response.json();
      const campaign = Array.isArray(responseJson.campaigns)
        ? responseJson.campaigns.find(item => item?.displayId === this.order.campaignDisplayId)
        : null;

      this.campaignOfficialPeriod = campaign
        ? { officialFrom: campaign.officialFrom, officialTo: campaign.officialTo }
        : null;
    } catch (error) {
      console.error('Error fetching campaign period:', error);
      this.campaignOfficialPeriod = null;
    }

    this.renderCampaignPeriod();
  }

  renderCampaignPeriod() {
    const officialFrom = window.convertDateToDanishFormat(this.campaignOfficialPeriod?.officialFrom);
    const officialTo = window.convertDateToDanishFormat(this.campaignOfficialPeriod?.officialTo);
    const displayPeriod = officialFrom && officialTo ? `${officialFrom} - ${officialTo}` : '-';

    this.querySelectorAll('.order-campaign-period').forEach(element => {
      element.textContent = displayPeriod;
    });
  }

  renderOrderSummary() {
    const order = this.order;

    if (order.campaignDisplayId) {
      if (!order.axCompleteStatus) {
        this.querySelector('[data-action="complete-order"]').removeAttribute('hidden');
      }
    }
    
    this.querySelectorAll('.order-number').forEach(element => {
      element.textContent = order.axOrderNumber;
    });

    this.querySelectorAll('.order-created-date').forEach(element => {
      element.textContent = order.createdAt ? window.convertDateToDanishFormat(order.createdAt) : '-';
    });

    this.querySelectorAll('.order-delivered-to').forEach(element => {
      element.textContent = order.deliveredTo ? order.deliveredTo : '';
    });

    this.querySelectorAll('.order-status').forEach(element => {
      let textStatus = '';
      let statusClass = 'status-color-red';
      switch (order.orderStatus) {
        // case 'UNFULFILLED':
        //   textStatus = window.opOrderDetailsTexts.status_unfulfilled;
        //   statusClass = 'status-color-green'
        //   break;
        // case 'PARTIALLY_FULFILLED':
        //   textStatus = window.opOrderDetailsTexts.status_partially_fulfilled;
        //   statusClass = 'status-color-green';
        //   break;
        // case 'FULFILLED':
        //   textStatus = window.opOrderDetailsTexts.status_fulfilled;
        //   statusClass = 'status-color-red';
        //   break;
        // case 'PARTIALLY_FULFILLED_LOCKED':
        //   textStatus = window.opOrderDetailsTexts.status_partially_fulfilled_locked;
        //   statusClass = 'status-color-yellow';
        //   break;
        // case 'UNFULFILLED_LOCKED':
        //   textStatus = window.opOrderDetailsTexts.status_unfulfilled_locked;
        //   statusClass = 'status-color-yellow';
        //   break;
        case 'SHIPPING':
          textStatus = window.opOrderDetailsTexts.status_shipping;
          statusClass = 'status-color-yellow';
          break;
        case 'ORDERING':
          textStatus = window.opOrderDetailsTexts.status_ordering;
          statusClass = 'status-color-green';
          const orderToExpired = order.orderTo && (() => {
            const deadline = new Date(order.orderTo + 'T23:59:59');
            return deadline < new Date();
          })();
          if (this.querySelector('[data-action="edit-order"]') && !orderToExpired) {
            this.querySelector('[data-action="edit-order"]').classList.remove('hidden');
          }
          break;
        case 'SHIPPED':
          textStatus = window.opOrderDetailsTexts.status_shipped;
          statusClass = 'status-color-red';
          break;
      }

      element.textContent = textStatus;
      element.classList.add(statusClass);
    });

    this.querySelectorAll('.order-picked-status').forEach(element => {
      let pickedText = '';
      if (order.orderStatus === 'ORDERING') {
        const orderLines = order.orderLines || [];
        const allUnlocked = orderLines.every(line => !line.isLocked);
        if (allUnlocked) {
          pickedText = window.opOrderDetailsTexts.picked_no;
        } else {
          pickedText = window.opOrderDetailsTexts.picked_partially;
        }
      } else {
        pickedText = window.opOrderDetailsTexts.picked_yes;
      }
      element.textContent = pickedText;
    });

    this.querySelectorAll('.order-campaign-id').forEach(element => {
      element.textContent = order.campaignDisplayId ? order.campaignDisplayId : '';
    });

    this.querySelectorAll('.order-campaign-name').forEach(element => {
      element.textContent = order.campaignName ? order.campaignName : '';
    });

    this.querySelectorAll('.order-campaign-best-period').forEach(element => {
      let orderFrom = window.convertDateToDanishFormat(order.orderFrom);
      let orderTo = window.convertDateToDanishFormat(order.orderTo);
      if (orderFrom && orderTo) {
        element.textContent = orderFrom + ' ' + window.opOrderDetailsTexts.to + ' ' + orderTo;
      } else if (orderFrom) {
        element.textContent = window.opOrderDetailsTexts.from + ' ' + orderFrom;
      } else if (orderTo) {
        element.textContent = window.opOrderDetailsTexts.to + ' ' + orderTo;
      }
    });

    this.querySelectorAll('.order-campaign-completed').forEach(element => {
      element.textContent = order.axCompleteStatus ? window.opOrderDetailsTexts.yes : window.opOrderDetailsTexts.no;
    });

    if (order.campaignName || order.orderFrom || order.orderTo || order.campaignDisplayId) {
      this.querySelector('.order-summary-item-campaign').classList.remove('hidden');
    } else {
      this.querySelector('.order-summary-item-campaign').classList.add('hidden');
    }

    this.querySelectorAll('.order-billing-address').forEach(element => {
      let address = order.invoiceAddress;
      if (address) {
        element.innerHTML = `<div>${address.name ? address.name : ''}</div><div>${address.company ? address.company + ', ' : ''}${address.address1 ? address.address1 : ''}</div><div>${address.city ? address.city + ' ' : ''}${address.zip ? address.zip : ''}</div>${address.address2 ? `<div>${address.address2}</div>` : ''}`;
      } else {
        element.innerHTML = '-';
      }
    });

    this.querySelectorAll('.order-delivery-address').forEach(element => {
      let address = order.shippingAddress;
      if (address) {
        element.innerHTML = `<div>${address.name ? address.name : ''}</div><div>${address.company ? address.company + ', ' : ''}${address.address1 ? address.address1 : ''}</div><div>${address.city ? address.city + ' ' : ''}${address.zip ? address.zip : ''}</div>${address.address2 ? `<div>${address.address2}</div>` : ''}`;
      } else {
        element.innerHTML = '-';
      }
    });

    this.querySelectorAll('.order-total-price-ex-vat').forEach(element => {
      element.textContent = order.subtotalExcludingTaxes ? parseFloat(order.subtotalExcludingTaxes).toFixed(2).replace('.', ',') + ' DKK' : '0,00';
    });

    this.querySelectorAll('.order-total-vat').forEach(element => {
      element.textContent = order.totalPriceSet && order.totalPriceSet > 0 &&order.currentTotalTaxSet ? parseFloat(order.currentTotalTaxSet).toFixed(2).replace('.', ',') + ' DKK' : '0,00';
    });

    this.querySelectorAll('.order-total-price').forEach(element => {
      element.textContent = order.totalPriceSet ? parseFloat(order.totalPriceSet).toFixed(2).replace('.', ',') + ' DKK' : '0,00';
    });
  }

  async renderOrderProducts() {
    const order = this.order;
    if (order && order.orderLines && order.orderLines.length > 0  ) {
      const table = this.querySelector('#op-order-details-table');
      const vamkuOrder = {
        ...order,
        officialTo: this.campaignOfficialPeriod?.officialTo
      };
      const showVamkuColumn = isVamkuPeriodActive(vamkuOrder);
      if (showVamkuColumn) {
        table?.style.setProperty('--table-header-length', '9');
        table?.style.setProperty('--grid-template-columns', '5% 25% 13% 13% 10% 9% 10% 10% 5%');
        this.querySelector('[data-vamku-column]')?.removeAttribute('hidden');
      }
      table?.classList.add('op-loading');
      let tableData = [];
      const productDataBySku = await this.getProductDataBySku(order.orderLines.map(line => line.sku));
      order.orderLines.forEach(line => {
        let product = productDataBySku.get(line.sku) || null;
        let variant = product?.variants.find(variant => variant.sku === line.sku) || null;
        let variantId = variant?.id || line.id;
        let lineItemType = product?.type || '';
        let lineItemSortiment = product?.metafields?.sortiment?.join(', ') || '';
        let lineItemVaretype = product?.metafields?.varetype || '';
        let customerRef = line.customerReference || '';
        let moreInfoHtml = `
          <div class="more-data-item grid grid-flow-col gap-x-6 items-start justify-start">
            <div class="line-item-disponent"><strong>${window.opOrderDetailsTexts.disponent}</strong> <span>${lineItemType}</span></div>
            <div class="line-item-type"><strong>${window.opOrderDetailsTexts.type}</strong> <span>${lineItemVaretype}</span></div>
            <div class="line-item-sortiment"><strong>${window.opOrderDetailsTexts.sortiment}</strong> <span>${lineItemSortiment}</span></div>
            <div class="line-item-customer-ref">
              <strong>${window.opOrderDetailsTexts.customer_ref}</strong> 
              <span data-customer-ref="${line.customerReference ? line.customerReference : ''}" class="${!line.editable || line.isLocked || line.fulfillmentStatus == 'fulfilled' ? 'always-visible' : ''}">${customerRef}</span>
              <input type="text" name="customer-ref-input" class="line-item-customer-ref-input input" ${!line.editable || line.isLocked || line.fulfillmentStatus == 'fulfilled' ? 'disabled' : ''} value="${line.customerReference ? line.customerReference : ''}">
            </div>
          </div>
        `;

        let statusClass = line.fulfillmentStatus == 'fulfilled' ? 'unavailable' : 'available';
        if (line.lockingRule == 'LOCKED' && line.isLocked) {
          statusClass = 'unavailable';
        }
        if (!line.editable) {
          statusClass = 'unavailable';
        }
        if (order.orderStatus == 'SHIPPING' || order.orderStatus == 'SHIPPED') {
          statusClass = 'unavailable';
        }
        let statusHtml = `<div class="op-prod-status ${statusClass}" data-editable="${line.editable}"></div>`;

        let imageHtml = variant?.featuredImage ? `<img src="${variant.featuredImage}" alt="${product.productTitle}" class="op-product-image">` : '';
        if (!imageHtml) {
          imageHtml = product?.productImage ? `<img src="${product.productImage}" alt="${product.productTitle}" class="op-product-image">` : '';
        }

        let nameAndImageHtml = line.title;
        if (product && variant) {
          nameAndImageHtml = `<div class="flex gap-2">
            ${imageHtml ? `<a href="/products/${product.productHandle}" target="_blank">${imageHtml}</a>` : ''}
            <div class="flex flex-col">
              <a href="/products/${product.productHandle}" target="_blank" class="no-underline font-bold">${product.productTitle}</a>
              ${product.categoryName ? `<div class="font-normal">${product.categoryName}</div>` : ''}
            </div>
          </div>`;
        }

        let skuAndBarcodeHtml = `<div class="flex flex-col">
            <div>${line.sku}</div>
            ${variant?.barcode ? `<div>${variant.barcode}</div>` : ''}
          </div>`;

        let minQty = variant?.quantityRule?.minimum || 1;
        let maxQty = variant?.quantityRule?.maximum || null;
        let incrementQty = variant?.quantityRule?.increment || 1;
        let removeBtnClass = '';
        if (line.lockingRule == 'CAN_INCREASE') {
          minQty = line.currentQuantity;
          removeBtnClass = 'disabled';
        } else if (line.lockingRule == 'CAN_DECREASE') {
          minQty = 0;
          maxQty = line.currentQuantity;
        } else if (line.lockingRule == 'LOCKED') {
          minQty = line.currentQuantity;
          maxQty = line.currentQuantity;
          removeBtnClass = 'disabled';
        }

        let quantityHtml = `
          <div class="flex flex-col">
            <div class="order-original-quantity" data-locked="${!line.editable || line.isLocked || line.fulfillmentStatus == 'fulfilled'}">${line.currentQuantity ? line.currentQuantity : '0'}</div>
            <div class="order-editing-form-item" data-locked="${!line.editable || line.isLocked || line.fulfillmentStatus == 'fulfilled'}" data-locking-rule="${line.lockingRule}">
              <quantity-input class="inline-block">
                <div class="qty-input qty-input--combined inline-flex items-center w-full">
                  <button type="button" class="qty-input__btn btn btn--minus no-js-hidden" name="minus" aria-controls="op-oi-vid-${variantId}" ${line.lockingRule == 'LOCKED' ? 'disabled' : ''}>
                    <span class="visually-hidden">Decrease quantity</span>
                  </button>
                  <input type="number" class="qty-input__input input"
                    id="op-oi-vid-${variantId}"
                    name="quantity"
                    min="${minQty}"
                    step="${incrementQty}"
                    ${maxQty !== null && maxQty !== undefined ? `max="${maxQty}"` : ''}
                    value="${line.currentQuantity}"
                    data-vid="${variantId}"
                    data-line-item-id="${line.id}"
                    data-locking-rule="${line.lockingRule}"
                    data-line-type="${line.type || ''}"
                    ${line.lockingRule == 'LOCKED' ? 'disabled' : ''}
                  >
                  <button type="button" class="qty-input__btn btn btn--plus no-js-hidden" name="plus" aria-controls="op-oi-vid-${variantId}" ${line.lockingRule == 'LOCKED' ? 'disabled' : ''}>
                    <span class="visually-hidden">Increase quantity</span>
                  </button>
                </div>
              </quantity-input>
            </div>
          </div>
        `;

        let actionsHtml = `
          <div class="actions flex gap-2 justify-end">
            <a href="#" data-vid="${variantId}" class="op-product-remove order-editing-form-item ${removeBtnClass}" data-locked="${!line.editable || line.isLocked || line.fulfillmentStatus == 'fulfilled'}" data-line-type="${line.type || ''}">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3346 7.5V15.8333H6.66797V7.5H13.3346ZM12.0846 2.5H7.91797L7.08464 3.33333H4.16797V5H15.8346V3.33333H12.918L12.0846 2.5ZM15.0013 5.83333H5.0013V15.8333C5.0013 16.75 5.7513 17.5 6.66797 17.5H13.3346C14.2513 17.5 15.0013 16.75 15.0013 15.8333V5.83333Z" fill="black"/>
              </svg>
            </a>
            <a href="#" data-action="toggle-line-details">
              <svg width="21" height="12" viewBox="0 0 21 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="-0.75" x2="14.1626" y2="-0.75" transform="matrix(-0.708126 0.706086 -0.693529 -0.720429 19.168 0)" stroke="#DF2627" stroke-width="1.5"/>
                <line y1="-0.75" x2="14.058" y2="-0.75" transform="matrix(-0.717026 -0.697047 0.684345 -0.729158 11.25 10)" stroke="#DF2627" stroke-width="1.5"/>
              </svg>
            </a>
          </div>
        `;

        let stockStatusHtml = `<div class="text-sm inventory-store-customer hidden" data-product-id="${product?.id || ''}" data-variant-sku="${variant?.sku || line.sku}" data-next-arrival-date="${product?.metafields?.nextArrivalDate || ''}">
              <div class="flex flex-wrap gap-1"><b>IR:</b> <span class="inventory-store-ir-value inventory-store-value out-stock">0 / 0</span></div>
              <div class="flex flex-wrap gap-1"><b>Butik:</b> <span class="inventory-store-customer-value inventory-store-value out-stock">0 / 0 / 0</span></div>
            </div>`;

        const row = [
          statusHtml,
          nameAndImageHtml,
          skuAndBarcodeHtml,
          stockStatusHtml,
          quantityHtml,
          line.discountedUnitPriceAfterAllDiscountsSet ? parseFloat(line.discountedUnitPriceAfterAllDiscountsSet).toFixed(2).replace('.', ',') : ''
        ];
        if (showVamkuColumn) {
          row.push(getVamkuDisplayPrice({
            order: vamkuOrder,
            entries: product?.metafields?.vamku,
            sku: line.sku
          }));
        }
        row.push(
          line.currentTotalPriceSet ? parseFloat(line.currentTotalPriceSet).toFixed(2).replace('.', ',') : '0,00',
          actionsHtml,
          moreInfoHtml
        );
        tableData.push(row);
      });
      if (table && tableData.length && typeof table.setRows === 'function') {
        table.classList.remove('hidden');
        table.setRows(tableData);
      }
      table?.classList.remove('op-loading');

      // render stock status
      document.dispatchEvent(new CustomEvent('ir:stock-references:fetch', {}));

      // blur line which are not editable. They are removed line from the order.
      this.querySelectorAll('.op-prod-status[data-editable="false"]').forEach(item => {
        let parentRow = item.closest('.op-table-row');
        if (parentRow) {
          parentRow.classList.add('removed-line');
        }
      });
    }
  }

  async getProductDataBySku(skus) {
    this.productDataBySku ??= new Map();
    const missingSkus = skus.filter(sku => sku && !this.productDataBySku.has(sku));
    if (missingSkus.length) {
      const products = await this.fetchExtraData(missingSkus);
      for (const product of products) {
        for (const variant of product?.variants || []) {
          if (variant?.sku) this.productDataBySku.set(variant.sku, product);
        }
      }
    }
    return this.productDataBySku;
  }

  async fetchExtraData(allSkus) {
    const BATCH_SIZE = 20;
    let extraData = [];
    const uniqueSkus = Array.from(new Set(allSkus.filter(sku => !!sku)));
    const batches = [];
    for (let i = 0; i < uniqueSkus.length; i += BATCH_SIZE) {
      batches.push(uniqueSkus.slice(i, i + BATCH_SIZE));
    }
    const results = await Promise.all(batches.map(batch => {
      const searchQuery = batch.map(sku => `variants.sku:${sku}`).join(' OR ');
      return fetch(`/search?q=${encodeURIComponent(searchQuery)}&type=product&view=product-details&options[prefix]=none`)
        .then(response => response.json())
        .catch(error => {
          console.error('Error fetching extra product data:', error);
          return [];
        });
    }));
    const seenProductIds = new Set();
    for (const batchResult of results) {
      if (!Array.isArray(batchResult)) continue;
      for (const product of batchResult) {
        const key = product?.id || product?.productHandle;
        if (key && seenProductIds.has(key)) continue;
        if (key) seenProductIds.add(key);
        extraData.push(product);
      }
    }
    return extraData;
  }

  confirmRemoveLine(onConfirm, onCancel = null, opener = null) {
    const title = window.opOrderDetailsTexts.remove_line_confirm_title;
    const message = window.opOrderDetailsTexts.remove_line_confirm_message;
    const popup = this.querySelector('ir-popup') || document.querySelector('ir-popup');
    if (popup?.show) {
      popup.show({
        type: 'confirm',
        title,
        message,
        onConfirm,
        onCancel
      }, opener);
      return;
    }

    if (window.confirm(message)) {
      onConfirm();
      return;
    }

    if (typeof onCancel === 'function') {
      onCancel();
    }
  }

  handleActions() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-action="toggle-line-details"]')) {
        event.preventDefault();
        event.target.closest('.op-table-row').classList.toggle('show-more-data');
      }
      if (event.target.closest('[data-action="edit-order"]')) {
        event.preventDefault();
        this.classList.add('editing-order');
        this.showEditNotice();
      }
      if (event.target.closest('[data-action="save-editing-order"]')) {
        event.preventDefault();
        this.saveOrder();
      }
      if (event.target.closest('[data-action="cancel-editing-order"]')) {
        event.preventDefault();
        this.cancelEditing();
      }
      if (event.target.closest('[data-action="reload-order-data"]')) {
        event.preventDefault();
        this.reloadOrderData();
      }
      if (event.target.closest('.op-product-remove')) {
        event.preventDefault();
        const removeButton = event.target.closest('.op-product-remove');
        if (removeButton.classList.contains('disabled')) {
          return;
        }
        const row = removeButton.closest('.op-table-row');
        if (row) {
          const removeLine = () => {
            row.classList.add('removing-line');
            this.handleLineEdited();
          };
          if (removeButton.dataset.lineType == 'SHOPIFY') {
            this.confirmRemoveLine(removeLine, null, removeButton);
          } else {
            removeLine();
          }
        }
      }
      if (event.target.closest('[data-action="complete-order"]')) {
        event.preventDefault();
        this.handleCompleteOrder();
      }
    });

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]')) {
        const input = event.target;
        let quantity = parseInt(input.value, 10);
        if (input.getAttribute('min') && (quantity < parseInt(input.getAttribute('min'), 10) || isNaN(quantity))) {
          input.value = input.getAttribute('min');
          quantity = parseInt(input.value, 10);
        }
        if (input.getAttribute('max') && quantity > parseInt(input.getAttribute('max'), 10)) {
          input.value = input.getAttribute('max');
          quantity = parseInt(input.value, 10);
        }
        const previousQuantity = parseInt(input.dataset.previousQuantity || input.defaultValue, 10);
        if (quantity === 0 && previousQuantity > 0) {
          const confirmQuantity = () => {
            input.dataset.previousQuantity = input.value;
            this.handleLineEdited();
          };
          if (input.dataset.lineType == 'SHOPIFY') {
            this.confirmRemoveLine(confirmQuantity, () => {
              input.value = previousQuantity;
            }, input);
          } else {
            confirmQuantity();
          }
          return;
        }
        input.dataset.previousQuantity = input.value;
        this.handleLineEdited();
      }
      if (event.target.matches('input[name="customer-ref-input"]')) {
        this.handleLineEdited();
      }
    });

    // Remember the row whose +/- was clicked: Safari/Firefox on macOS don't focus buttons on click,
    // so the following Tab keydown lands on <body> instead of the button
    this.addEventListener('click', (event) => {
      const qtyButton = event.target.closest('.op-table-row .qty-input__btn');
      if (qtyButton) {
        this.lastQuantityInput = qtyButton.closest('quantity-input')?.querySelector('input[name="quantity"]');
      }
    });
    document.addEventListener('focusin', (event) => {
      if (!event.target.closest?.('.op-table-row .qty-input__btn')) this.lastQuantityInput = null;
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      const input = this.getTabSourceQuantityInput(event.target);
      if (input) this.focusAdjacentQuantityInput(event, input);
    });
  }

  getTabSourceQuantityInput(target) {
    if (!this.classList.contains('editing-order')) return null;
    if (this.contains(target)) {
      if (target.matches('.op-table-row input[name="quantity"]')) return target;
      if (target.matches('.op-table-row .qty-input__btn')) {
        return target.closest('quantity-input')?.querySelector('input[name="quantity"]');
      }
      return null;
    }
    if ((target === document.body || target === document.documentElement) && this.contains(this.lastQuantityInput)) {
      return this.lastQuantityInput;
    }
    return null;
  }

  getEditableQuantityInputs() {
    return Array.from(this.querySelectorAll('.op-table-row input[name="quantity"]')).filter(input =>
      !input.disabled &&
      !input.closest('[inert]') &&
      !input.closest('.order-editing-form-item[data-locked="true"]') &&
      input.getClientRects().length > 0
    );
  }

  // Tab / Shift+Tab jumps between quantity inputs instead of the +/- buttons and row actions
  focusAdjacentQuantityInput(event, input) {
    const direction = event.shiftKey ? -1 : 1;
    const inputs = this.getEditableQuantityInputs();
    const index = inputs.indexOf(input);
    if (index === -1 || !inputs[index + direction]) return;

    event.preventDefault();
    this.lastQuantityInput = null;
    if (document.activeElement === input) {
      // Blur first so the change handler runs (it can lock rows at the line limit or open the remove-line popup)
      input.blur();
      if (document.activeElement && document.activeElement !== document.body) return;
    }

    const nextInputs = this.getEditableQuantityInputs();
    const currentIndex = nextInputs.indexOf(input);
    const nextInput = currentIndex === -1 ? null : nextInputs[currentIndex + direction];
    if (!nextInput) return;
    nextInput.focus();
    nextInput.select();
  }

  getRowStates() {
    return Array.from(this.querySelectorAll('.op-table-row')).map(row => {
      const quantityInput = row.querySelector('.qty-input__input');
      return {
        lineItemId: quantityInput?.getAttribute('data-line-item-id'),
        currentQuantity: quantityInput?.value,
        customerReference: row.querySelector('.line-item-customer-ref input[name="customer-ref-input"]')?.value,
        removing: row.classList.contains('removing-line'),
        removed: row.classList.contains('removed-line')
      };
    });
  }

  handleLineEdited() {
    if (this.isEditingBlocked) return;
    this.showEditNotice();
  }

  updateChangedLines() {
    const changedLineIds = getChangedLineIds(this.getRowStates(), this.order?.orderLines);
    const limitReached = changedLineIds.size >= ORDER_EDIT_LINE_LIMIT;
    this.querySelectorAll('.op-table-row').forEach(row => {
      const lineItemId = row.querySelector('.qty-input__input')?.getAttribute('data-line-item-id');
      const changed = changedLineIds.has(lineItemId);
      const locked = limitReached && !changed;
      row.classList.toggle('has-changes', changed);
      row.classList.toggle('edit-locked', locked);
      row.inert = locked;
    });
    return changedLineIds.size;
  }

  showEditNotice() {
    const texts = window.opOrderDetailsTexts;
    const changedLineCount = this.updateChangedLines();
    const limitReached = changedLineCount >= ORDER_EDIT_LINE_LIMIT;
    const message = (limitReached ? texts.order_edit_limit_reached : texts.order_edit_notice)
      .replaceAll('[limit]', ORDER_EDIT_LINE_LIMIT)
      .replace('[count]', changedLineCount);
    this.showSaveBanner(limitReached ? 'warning' : 'info', message, {
      showSaveButton: changedLineCount > 0
    });
  }

  async saveOrder() {
    if (this.isEditingBlocked) return;

    const texts = window.opOrderDetailsTexts;
    const rowStates = this.getRowStates();
    const changedLineCount = getChangedLineIds(rowStates, this.order?.orderLines).size;
    if (changedLineCount === 0) {
      this.classList.remove('editing-order');
      this.resetSaveBanner();
      return;
    }
    const continueEditing = changedLineCount >= ORDER_EDIT_LINE_LIMIT;

    this.isSaving = true;
    this.setEditingBlocked(true);
    this.showSaveBanner('saving', texts.order_saving.replace('[count]', changedLineCount));

    try {
      const response = await fetch(`/apps/ax/order-portal/retailers/${this.retailerId}/orders/${this.orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(buildOrderUpdatePayload(rowStates))
      });
      const data = await response.json();
      if (!data.order?.orderId) {
        throw new Error(texts.order_update_error);
      }
      await this.fetchOrderDetails({ soft: true });
    } catch (error) {
      console.error(`Error updating order ${this.orderId}:`, error);
      this.showSaveBanner('error', texts.order_save_failed_may_continue, { showReloadButton: true });
      return;
    } finally {
      this.isSaving = false;
    }

    if (!continueEditing) {
      this.classList.remove('editing-order');
    }
    this.setEditingBlocked(false);
    this.showSaveBanner('success', continueEditing ? texts.order_saved_continue_editing : texts.order_updated_successfully, {
      hideAfterMs: SAVE_BANNER_HIDE_DELAY_MS
    });
  }

  async reloadOrderData() {
    if (this.isSaving) return;
    this.isSaving = true;
    const reloadButton = this.querySelector('[data-action="reload-order-data"]');
    reloadButton?.classList.add('is-loading');
    reloadButton?.setAttribute('disabled', '');
    try {
      await this.fetchOrderDetails({ soft: true });
      this.setEditingBlocked(false);
      this.resetSaveBanner();
    } catch (error) {
      console.error(`Error reloading order ${this.orderId}:`, error);
    } finally {
      this.isSaving = false;
      reloadButton?.classList.remove('is-loading');
      reloadButton?.removeAttribute('disabled');
    }
  }

  async cancelEditing() {
    if (this.isEditingBlocked) return;
    this.classList.remove('editing-order');
    this.resetSaveBanner();
    await this.renderOrderProductsPreservingView();
  }

  setEditingBlocked(blocked) {
    this.isEditingBlocked = blocked;
    this.classList.toggle('is-saving', blocked);
    this.toggleAttribute('aria-busy', blocked);
    const table = this.querySelector('#op-order-details-table');
    const headerActions = this.querySelector('.op-header-actions');
    if (table) table.inert = blocked;
    if (headerActions) headerActions.inert = blocked;

    this.querySelectorAll('[data-action="save-editing-order"]').forEach(saveButton => {
      saveButton.classList.toggle('is-loading', blocked);
      saveButton.toggleAttribute('disabled', blocked);
    });
  }

  showSaveBanner(type, message, { showReloadButton = false, showSaveButton = false, hideAfterMs = 0 } = {}) {
    const banner = this.querySelector('.op-order-save-banner');
    if (!banner) return;

    clearTimeout(this.saveBannerTimeout);
    // Orders with fewer lines than the limit can never reach it, so only the floating save button is shown.
    // Errors still show in full, since the reload button is the only way out of the blocked editing state.
    const compact = type !== 'error' && (this.order?.orderLines?.length || 0) < ORDER_EDIT_LINE_LIMIT;
    if (compact) {
      // Keep the button visible while saving so its loading state shows
      showSaveButton = showSaveButton || type === 'saving';
      if (!showSaveButton) {
        banner.hidden = true;
        return;
      }
    }
    banner.classList.toggle('op-order-save-banner--compact', compact);
    banner.classList.remove(
      'op-order-save-banner--info',
      'op-order-save-banner--warning',
      'op-order-save-banner--saving',
      'op-order-save-banner--success',
      'op-order-save-banner--error'
    );
    banner.classList.add(`op-order-save-banner--${type}`);
    banner.querySelector('.op-order-save-banner__message').textContent = message;
    const reloadButton = banner.querySelector('[data-action="reload-order-data"]');
    if (reloadButton) reloadButton.hidden = !showReloadButton;
    const saveButton = banner.querySelector('[data-action="save-editing-order"]');
    if (saveButton) saveButton.hidden = !showSaveButton;
    banner.hidden = false;

    if (hideAfterMs) {
      this.saveBannerTimeout = setTimeout(() => this.resetSaveBanner(), hideAfterMs);
    }
  }

  resetSaveBanner() {
    clearTimeout(this.saveBannerTimeout);
    if (this.classList.contains('editing-order') && !this.isEditingBlocked) {
      this.showEditNotice();
      return;
    }
    const banner = this.querySelector('.op-order-save-banner');
    if (banner) banner.hidden = true;
  }

  handleCompleteOrder() {
    if (this.isCompleting) return;
    if (!this.orderId) {
      alert(window.opOrderDetailsTexts.order_complete_error);
      return;
    }
    this.isCompleting = true;
    try {
      console.log('complete order');
      const completeBtn = this.querySelector('[data-action="complete-order"]');
      completeBtn.classList.add('is-loading');
      completeBtn.setAttribute('disabled', '');
      fetch(`/apps/ax/order-portal/retailers/${this.retailerId}/orders/${this.orderId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.order?.completed) {
          alert(window.opOrderDetailsTexts.order_completed_successfully);
          window.location.reload();
        } else {
          alert(window.opOrderDetailsTexts.order_complete_error);
          completeBtn.classList.remove('is-loading');
          completeBtn.removeAttribute('disabled');
          this.isCompleting = false;
        }
      })
      .catch(error => {
        alert(error.message);
        completeBtn.classList.remove('is-loading');
        completeBtn.removeAttribute('disabled');
        this.isCompleting = false;
      })
    } catch (error) {
      this.isCompleting = false;
      alert(error.message);
    }
  }
}

customElements.define('op-order-details', OpOrderDetails);